import { loadStripe } from '@stripe/stripe-js';
import React from 'react';
import { Redirect, useParams } from 'react-router-dom';

import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal/Modal';
import {
  PaymentMethod,
  PaymentSessionResult
} from '../../../generated/graphql';
import getEnv from '../../../utils/getEnv';
import { InitiatePaymentParams } from '../InitiatePayment';

const {
  REACT_APP_STRIPE_CA_PUBLISHABLE_KEY,
  REACT_APP_STRIPE_US_PUBLISHABLE_KEY
} = getEnv();

const SUPPORTED_PAYMENT_METHOD = new Set([PaymentMethod.CreditCard]);

interface PaymentRedirectModalProps {
  paymentMethod: PaymentMethod;
  paymentSessionResult: PaymentSessionResult | undefined;
  setClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const PaymentRedirectModal: React.FC<PaymentRedirectModalProps> = ({
  paymentMethod,
  paymentSessionResult,
  setClose
}) => {
  const { invoiceId: invoiceIdFromUrl } = useParams<InitiatePaymentParams>();
  if (!SUPPORTED_PAYMENT_METHOD.has(paymentMethod)) {
    throw new Error(`unreachable: ${paymentMethod} not supported`);
  }

  const paymentProvider = 'Stripe';

  const handlePaymentModalNext = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    switch (paymentSessionResult?.__typename) {
      case 'StripeData':
        const publishableKey =
          paymentSessionResult.stripePlatformAccount ===
          'StripePlatformAccount-US'
            ? REACT_APP_STRIPE_US_PUBLISHABLE_KEY
            : REACT_APP_STRIPE_CA_PUBLISHABLE_KEY;
        const stripe = await loadStripe(publishableKey);
        /* istanbul ignore else -- defensive check: loadStripe should not return null */
        if (stripe) {
          await stripe.redirectToCheckout({
            sessionId: paymentSessionResult.session_id
          });
        } else {
          throw new Error(`unreachable: Stripe Redirect failed`);
        }
        break;
      /* istanbul ignore next -- defensive check: all valid typenames handled above */
      default:
        throw new Error('unexpected: expected StripeData');
    }
  };

  const PaymentRedirectModalComponent = () => (
    <div
      className="PaymentRedirectModal w-full"
      data-testid="PaymentRedirectModal"
    >
      <Modal
        setClose={setClose}
        onAction={handlePaymentModalNext}
        actionName={'NEXT'}
      >
        <div className="px-8 py-12">
          <p>
            {`${paymentProvider} processes the payments for Foxquilt. After clicking
            NEXT you will be redirected to a secure payment page hosted by
            ${paymentProvider}.`}
          </p>
          <p>Your information will not be shared with any third party.</p>
        </div>
      </Modal>
    </div>
  );

  if (!paymentSessionResult) {
    return <Loading />;
  } else {
    if (
      paymentSessionResult.__typename === 'InactiveRenewalData' ||
      paymentSessionResult.__typename === 'ActiveRenewalData'
    ) {
      return <Redirect to={`/complete/${invoiceIdFromUrl}`} />;
    } else {
      return PaymentRedirectModalComponent();
    }
  }
};

export default PaymentRedirectModal;
